import { createBrowserRouter, createRoutesFromElements, Navigate, Route } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import Layout from '../components/Layout';
import RequireAdmin from '../components/RequireAdmin';
import RootRedirect from '../components/RootRedirect';
import SystemAgentDetails from '../pages/systemAgents/SystemAgentDetails';
import Tools from '../pages/tools';
import Prompts from '../pages/prompts';
import Configs from '../pages/configs';
import Metrics from '../pages/metrics';
import Dashboards from '../pages/dashboards';
import CanvasPage from '../pages/canvas';
import AdminBusinessInsightsDashboard from '../pages/admin/dashboard';
import DashboardIndexRedirect from '../pages/admin/dashboard/DashboardIndexRedirect';
import OrganizationDetail from '../pages/admin/organizations/OrganizationDetail';
import OrganizationDetailIndexRedirect from '../pages/admin/organizations/OrganizationDetail/OrganizationDetailIndexRedirect';
import PlatformCosts from '../pages/admin/platformCosts';
import {
    OrganizationSkillsScopeMount,
    PlatformSkillsScopeMount,
} from '../pages/skills/skillsScope';
import createLazyPage from '../components/LazyPage';
import getOrganizationServicesRoute from '../pages/admin/organizations/OrganizationDetail/getOrganizationServicesRoute';

const Builder = createLazyPage({ loadPage: () => import('../pages/builder') });
const CanvasEditor = createLazyPage({ loadPage: () => import('../pages/canvas/Editor') });
const AdminPlaybookBuilder = createLazyPage({
    loadPage: () => import('../pages/admin/playbooks/builder'),
});
const AdminPlaybookBuilderRedirect = createLazyPage({
    loadPage: () => import('../pages/admin/playbooks/builder/BuilderRedirect'),
});
const PlaybookStepSettings = createLazyPage({
    loadPage: () => import('../pages/admin/playbooks/builder/StepSettings'),
});
const Playground = createLazyPage({ loadPage: () => import('../pages/playground') });
const PlaygroundContent = createLazyPage({
    loadPage: () => import('../pages/playground/modules/PlaygroundContent'),
});
const Testing = createLazyPage({ loadPage: () => import('../pages/testing') });
const History = createLazyPage({ loadPage: () => import('../pages/history') });
const ConfigEditor = createLazyPage({ loadPage: () => import('../pages/configs/Editor') });
const PromptEditor = createLazyPage({ loadPage: () => import('../pages/prompts/Editor') });
const FileEditor = createLazyPage({
    loadPage: () => import('../pages/prompts/Editor/FileEditor'),
});
const SkillEditor = createLazyPage({ loadPage: () => import('../pages/skills/Editor') });
const SkillFileEditor = createLazyPage({
    loadPage: () => import('../pages/skills/Editor/SkillFileEditor'),
});
const SkillVersionViewer = createLazyPage({
    loadPage: () => import('../pages/skills/VersionViewer'),
});
const DashboardDetails = createLazyPage({
    loadPage: () => import('../pages/dashboards/Details'),
});
const MetricsList = createLazyPage({ loadPage: () => import('../pages/metrics/List') });
const MetricDetails = createLazyPage({ loadPage: () => import('../pages/metrics/Details') });
const MetricOverview = createLazyPage({
    loadPage: () => import('../pages/metrics/Details/Overview'),
});
const MetricSchema = createLazyPage({
    loadPage: () => import('../pages/metrics/Details/Schema'),
});
const MetricEvents = createLazyPage({
    loadPage: () => import('../pages/metrics/Details/Events'),
});
const CalculatedFields = createLazyPage({
    loadPage: () => import('../pages/metrics/Details/CalculatedFields'),
});
const AdminWorkflowOps = createLazyPage({
    loadPage: () => import('../pages/admin/workflowOps'),
});
const DashboardSummaryTab = createLazyPage({
    loadPage: () => import('../pages/admin/dashboard/SummaryTab'),
});
const DashboardGrowthTab = createLazyPage({
    loadPage: () => import('../pages/admin/dashboard/GrowthTab'),
});
const DashboardUnitEconomicsTab = createLazyPage({
    loadPage: () => import('../pages/admin/dashboard/UnitEconomicsTab'),
});
const DashboardRetentionTab = createLazyPage({
    loadPage: () => import('../pages/admin/dashboard/RetentionTab'),
});
const DashboardEngagementTab = createLazyPage({
    loadPage: () => import('../pages/admin/dashboard/EngagementTab'),
});
const DashboardOrganizationsTab = createLazyPage({
    loadPage: () => import('../pages/admin/dashboard/OrganizationsTab'),
});
const PlatformCostsOverview = createLazyPage({
    loadPage: () => import('../pages/admin/platformCosts/Overview'),
});
const PlatformCostsProvider = createLazyPage({
    loadPage: () => import('../pages/admin/platformCosts/Provider'),
});
const OrganizationDetailSubscriptionTab = createLazyPage({
    loadPage: () => import('../pages/admin/organizations/OrganizationDetail/SubscriptionTab'),
});
const OrganizationDetailCostsTab = createLazyPage({
    loadPage: () => import('../pages/admin/organizations/OrganizationDetail/CostsTab'),
});
const OrganizationDetailWorkflowsTab = createLazyPage({
    loadPage: () => import('../pages/admin/organizations/OrganizationDetail/WorkflowsTab'),
});
const AdminPlaybooks = createLazyPage({ loadPage: () => import('../pages/admin/playbooks') });
const AdminServices = createLazyPage({ loadPage: () => import('../pages/admin/services') });
const AdminServiceLanding = createLazyPage({
    loadPage: () => import('../pages/admin/services/ServiceLanding'),
});
const AdminServiceDetail = createLazyPage({
    loadPage: () => import('../pages/admin/services/ServiceDetail'),
});
const AdminServiceDetailIndexRedirect = createLazyPage({
    loadPage: () => import('../pages/admin/services/ServiceDetail/ServiceDetailIndexRedirect'),
});
const AdminServiceBillingTab = createLazyPage({
    loadPage: () => import('../pages/admin/services/ServiceDetail/BillingTab'),
});
const AdminServicePlaybooksTab = createLazyPage({
    loadPage: () => import('../pages/admin/services/ServiceDetail/PlaybooksTab'),
});
const AdminTeam = createLazyPage({ loadPage: () => import('../pages/admin/team') });
const AdminOrganizations = createLazyPage({
    loadPage: () => import('../pages/admin/organizations'),
});
const MicroApps = createLazyPage({
    loadPage: () => import('../pages/microApps'),
});
const MicroAppLanding = createLazyPage({
    loadPage: () => import('../pages/microApps/MicroAppLanding'),
});
const MicroAppDetail = createLazyPage({
    loadPage: () => import('../pages/microApps/MicroAppDetail'),
});
const AdminSystemAgents = createLazyPage({
    loadPage: () => import('../pages/admin/systemAgents'),
});
const SystemAgentStats = createLazyPage({
    loadPage: () => import('../pages/systemAgents/SystemAgentStats'),
});
const Workflows = createLazyPage({ loadPage: () => import('../pages/workflows') });
const Templates = createLazyPage({ loadPage: () => import('../pages/templates') });
const CreateTemplate = createLazyPage({
    loadPage: () => import('../pages/templates/CreateTemplate'),
});
const TemplateDetails = createLazyPage({
    loadPage: () => import('../pages/templates/TemplateDetails'),
});
const Secrets = createLazyPage({ loadPage: () => import('../pages/secrets') });
const Connections = createLazyPage({ loadPage: () => import('../pages/connections') });
const Sandboxes = createLazyPage({ loadPage: () => import('../pages/sandboxes') });
const Models = createLazyPage({ loadPage: () => import('../pages/models') });
const OrganizationTeam = createLazyPage({ loadPage: () => import('../pages/organizationTeam') });
const Files = createLazyPage({ loadPage: () => import('../pages/files') });
const BucketDetails = createLazyPage({
    loadPage: () => import('../pages/files/BucketDetails'),
});
const BucketContent = createLazyPage({
    loadPage: () => import('../pages/files/BucketDetails/BucketContent'),
});
const ToolsList = createLazyPage({ loadPage: () => import('../pages/tools/ToolsList') });
const Toolboxes = createLazyPage({ loadPage: () => import('../pages/tools/Toolboxes') });
const Skills = createLazyPage({ loadPage: () => import('../pages/skills') });
const SystemAgents = createLazyPage({ loadPage: () => import('../pages/systemAgents') });
const SystemAgentOverview = createLazyPage({
    loadPage: () => import('../pages/systemAgents/SystemAgentOverview'),
});
const SystemAgentHistory = createLazyPage({
    loadPage: () => import('../pages/systemAgents/SystemAgentHistory'),
});
const SystemAgentAccess = createLazyPage({
    loadPage: () => import('../pages/systemAgents/SystemAgentAccess'),
});
const SystemAgentErrors = createLazyPage({
    loadPage: () => import('../pages/systemAgents/SystemAgentErrors'),
});
const CanvasList = createLazyPage({ loadPage: () => import('../pages/canvas/List') });
const PromptsList = createLazyPage({ loadPage: () => import('../pages/prompts/List') });
const ConfigsList = createLazyPage({ loadPage: () => import('../pages/configs/List') });
const DashboardsList = createLazyPage({ loadPage: () => import('../pages/dashboards/List') });

const router = createBrowserRouter(
    createRoutesFromElements(
        <Route>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/admin" element={<RequireAdmin />}>
                <Route element={<AdminLayout />}>
                    <Route index element={<Navigate to="dashboard" replace />} />
                    <Route path="dashboard" element={<AdminBusinessInsightsDashboard />}>
                        <Route index element={<DashboardIndexRedirect />} />
                        <Route path="summary" element={<DashboardSummaryTab />} />
                        <Route path="growth" element={<DashboardGrowthTab />} />
                        <Route path="unit-economics" element={<DashboardUnitEconomicsTab />} />
                        <Route path="retention" element={<DashboardRetentionTab />} />
                        <Route path="engagement" element={<DashboardEngagementTab />} />
                        <Route path="organizations" element={<DashboardOrganizationsTab />} />
                    </Route>
                    <Route path="workflow-ops" element={<AdminWorkflowOps />} />
                    <Route path="playbooks" element={<AdminPlaybooks />} />
                    <Route path="services" element={<AdminServices />}>
                        <Route index element={<AdminServiceLanding />} />
                        <Route path=":serviceId" element={<AdminServiceDetail />}>
                            <Route index element={<AdminServiceDetailIndexRedirect />} />
                            <Route path="billing" element={<AdminServiceBillingTab />} />
                            <Route path="playbooks" element={<AdminServicePlaybooksTab />} />
                        </Route>
                    </Route>
                    <Route
                        path="playbooks/:playbookId/builder"
                        element={<AdminPlaybookBuilderRedirect />}
                    />
                    <Route
                        path="playbooks/:playbookId/versions/:versionId/builder"
                        element={<AdminPlaybookBuilder />}
                    />
                    <Route
                        path="playbooks/:playbookId/versions/:versionId/document"
                        element={<AdminPlaybookBuilder />}
                    />
                    <Route
                        path="playbooks/:playbookId/versions/:versionId/slides"
                        element={<AdminPlaybookBuilder />}
                    />
                    <Route
                        path="playbooks/:playbookId/versions/:versionId/builder/steps/:stepId"
                        element={<PlaybookStepSettings />}
                    />
                    <Route path="team" element={<AdminTeam />} />
                    <Route path="organizations" element={<AdminOrganizations />} />
                    <Route path="organizations/:organizationId" element={<OrganizationDetail />}>
                        <Route index element={<OrganizationDetailIndexRedirect />} />
                        {getOrganizationServicesRoute()}
                        <Route
                            path="subscription"
                            element={<OrganizationDetailSubscriptionTab />}
                        />
                        <Route path="costs" element={<OrganizationDetailCostsTab />} />
                        <Route path="workflows" element={<OrganizationDetailWorkflowsTab />} />
                    </Route>
                    <Route path="platform-costs" element={<PlatformCosts />}>
                        <Route index element={<PlatformCostsOverview />} />
                        <Route path=":providerId" element={<PlatformCostsProvider />} />
                    </Route>
                    <Route path="system-agents" element={<AdminSystemAgents />}>
                        <Route path=":systemAgentId" element={<SystemAgentDetails />}>
                            <Route index element={<SystemAgentOverview />} />
                            <Route path="history" element={<SystemAgentHistory />} />
                            <Route path="stats" element={<SystemAgentStats />} />
                            <Route path="errors" element={<SystemAgentErrors />} />
                        </Route>
                    </Route>
                    <Route element={<PlatformSkillsScopeMount />}>
                        <Route path="skills" element={<Skills />} />
                        <Route
                            path="skills/:skillId/versions/:versionId"
                            element={<SkillVersionViewer />}
                        />
                        <Route path="skills/:skillId" element={<SkillEditor />}>
                            <Route path="file/:fileId" element={<SkillFileEditor />} />
                        </Route>
                    </Route>
                    <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
                </Route>
            </Route>
            <Route path="/agents/:agentId" element={<Layout />}>
                <Route path="apps" element={<MicroApps />}>
                    <Route index element={<MicroAppLanding />} />
                    <Route path=":appSlug/*" element={<MicroAppDetail />} />
                </Route>
                <Route path="workflows" element={<Workflows />} />
                <Route path="templates" element={<Templates />} />
                <Route path="templates/new" element={<CreateTemplate />} />
                <Route path="templates/:templateId" element={<TemplateDetails />} />
                <Route
                    path="templates/:templateId/versions/:versionId"
                    element={<TemplateDetails />}
                />
                <Route path="secrets" element={<Secrets />} />
                <Route path="connections" element={<Connections />} />
                <Route path="sandboxes" element={<Sandboxes />} />
                <Route path="team" element={<OrganizationTeam />} />
                <Route path="models" element={<Models />} />
                <Route path="system-agents" element={<SystemAgents />}>
                    <Route path=":systemAgentId" element={<SystemAgentDetails />}>
                        <Route index element={<SystemAgentOverview />} />
                        <Route path="history" element={<SystemAgentHistory />} />
                        <Route path="stats" element={<SystemAgentStats />} />
                        <Route path="access" element={<SystemAgentAccess />} />
                        <Route path="errors" element={<SystemAgentErrors />} />
                    </Route>
                </Route>
                <Route path="files" element={<Files />} />
                <Route path="files/:bucketName" element={<BucketDetails />}>
                    <Route index element={<BucketContent />} />
                    <Route path="*" element={<BucketContent />} />
                </Route>
                <Route path="prompts" element={<Prompts />}>
                    <Route index element={<PromptsList />} />
                    <Route path=":promptId" element={<PromptEditor />}>
                        <Route path="file/:fileId" element={<FileEditor />} />
                    </Route>
                </Route>
                <Route path="configs" element={<Configs />}>
                    <Route index element={<ConfigsList />} />
                    <Route path=":configId" element={<ConfigEditor />} />
                </Route>
                <Route path="metrics" element={<Metrics />}>
                    <Route index element={<MetricsList />} />
                    <Route path=":metricId" element={<MetricDetails />}>
                        <Route index element={<MetricOverview />} />
                        <Route path="schema" element={<MetricSchema />} />
                        <Route path="events" element={<MetricEvents />} />
                        <Route path="calculated-fields" element={<CalculatedFields />} />
                    </Route>
                </Route>
                <Route path="dashboards" element={<Dashboards />}>
                    <Route index element={<DashboardsList />} />
                    <Route path=":dashboardId" element={<DashboardDetails />} />
                </Route>
                <Route path="tools" element={<Tools />}>
                    <Route index element={<ToolsList />} />
                    <Route path="toolboxes" element={<Toolboxes />} />
                </Route>
                <Route path="canvas" element={<CanvasPage />}>
                    <Route index element={<CanvasList />} />
                    <Route path=":canvasId" element={<CanvasEditor />} />
                </Route>
                <Route element={<OrganizationSkillsScopeMount />}>
                    <Route path="skills" element={<Skills />} />
                    <Route
                        path="skills/:skillId/versions/:versionId"
                        element={<SkillVersionViewer />}
                    />
                    <Route path="skills/:skillId" element={<SkillEditor />}>
                        <Route path="file/:fileId" element={<SkillFileEditor />} />
                    </Route>
                </Route>
                <Route
                    path="workflows/:workflowId/versions/:versionId/builder"
                    element={<Builder />}
                />
                <Route
                    path="workflows/:workflowId/versions/:versionId/history"
                    element={<History />}
                />
                <Route
                    path="workflows/:workflowId/versions/:versionId/testing"
                    element={<Testing />}
                />
                <Route
                    path="workflows/:workflowId/versions/:versionId/testing/:testCaseId"
                    element={<Testing />}
                />
                <Route
                    path="workflows/:workflowId/versions/:versionId/:mode"
                    element={<Playground />}
                >
                    <Route index element={<PlaygroundContent />} />
                    <Route path="threads/:threadId" element={<PlaygroundContent />} />
                    <Route path="threads/:threadId/runs/:runId" element={<PlaygroundContent />} />
                    <Route
                        path="threads/:threadId/runs/:runId/events/:eventId"
                        element={<PlaygroundContent />}
                    />
                </Route>
                <Route path="*" element={<Navigate to="workflows" replace />} />
            </Route>
        </Route>
    )
);

export default router;
